import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

// The website toast (design DC:4052-4054, VIS §2.1 "Toast"; CB-17 `toast.tsx`).
//
// One dark pill at the bottom centre, auto-hidden after 2.6 s (the design's
// `toast()` timer, DC:4437-4441). A new message replaces the current one and restarts
// the timer — the design never stacks toasts on the website. The pill is lifted above
// the mobile tab bar by CSS (`.app-shell[data-tabbar] .toast`), which is the design's
// `toastLift`.
//
// It is a polite live region so the message is announced without stealing focus; the
// region stays mounted (empty) so screen readers are already listening when text
// arrives.

export const TOAST_MS = 2600;

interface ToastContextValue {
  show: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  // Bumped on every show so an identical message still re-plays the entry animation.
  const [seq, setSeq] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback((next: string) => {
    if (timer.current) clearTimeout(timer.current);
    setMessage(next);
    setSeq((n) => n + 1);
    timer.current = setTimeout(() => setMessage(null), TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {message && (
          <div key={seq} className="toast">
            {message}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
}

// Outside a provider (isolated component tests) the hook is a no-op rather than a
// throw: a toast is feedback, never the only record of an action.
const NOOP: ToastContextValue = { show: () => {} };

export function useToast(): ToastContextValue {
  return useContext(ToastContext) ?? NOOP;
}
